import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import particularesApiService from '../services/ParticularesApiService';
import brandLogo from '../assets/logos/logo_transparent_background.png';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import './ParticularAlumnoInvitationRegister.css';

const emptyForm = {
  email: '',
  nombre: '',
  apellido: '',
  telefono: '',
  dni: ''
};

const getName = (item, fallback = 'Particular') => item?.nombre || item?.Nombre || item?.displayName || item?.DisplayName || fallback;

const ParticularAlumnoInvitationRegister = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const initialCode = useMemo(() => (
    params.codigo || searchParams.get('codigo') || ''
  ), [params.codigo, searchParams]);

  const [codigo, setCodigo] = useState(initialCode);
  const [form, setForm] = useState(emptyForm);
  const [invitacion, setInvitacion] = useState(null);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [alert, setAlert] = useState({ show: false, title: '', message: '', type: 'error' });
  const captchaRef = useRef(null);
  const captchaWidgetId = useRef(null);

  const particular = invitacion?.particular || invitacion?.Particular || null;
  const particularName = getName(particular, 'Cuenta particular');
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAACZrRVcUHIKVxEDn';

  const validateCode = async (targetCode = codigo) => {
    const normalizedCode = String(targetCode || '').trim();
    if (!normalizedCode) {
      setInvitacion(null);
      setAlert({ show: true, type: 'error', title: 'Código requerido', message: 'Ingresá el código de invitación.' });
      return;
    }

    setValidating(true);
    setAlert((prev) => ({ ...prev, show: false }));
    try {
      const response = await particularesApiService.getAlumnoInvitacionPublica(normalizedCode);
      setInvitacion(response?.invitacion || response?.Invitacion || null);
      setCodigo(normalizedCode);
    } catch (error) {
      setInvitacion(null);
      setAlert({
        show: true,
        type: 'error',
        title: 'Invitación inválida',
        message: error.message || 'El código no existe o ya caducó.'
      });
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    if (initialCode) {
      validateCode(initialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCode]);

  useEffect(() => {
    if (window.turnstile || document.getElementById('turnstile-script')) return;

    const script = document.createElement('script');
    script.id = 'turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (captchaWidgetId.current !== null) return undefined;

    const timer = setInterval(() => {
      if (window.turnstile && captchaRef.current && captchaWidgetId.current === null) {
        captchaWidgetId.current = window.turnstile.render(captchaRef.current, {
          sitekey: turnstileSiteKey,
          callback: (token) => {
            setCaptchaToken(token);
            setCaptchaError('');
          },
          'expired-callback': () => {
            setCaptchaToken('');
            setCaptchaError('El captcha expiró. Completalo nuevamente.');
          },
          'error-callback': () => {
            setCaptchaToken('');
            setCaptchaError('Captcha inválido. Intentá nuevamente.');
          }
        });
        clearInterval(timer);
      }
    }, 200);

    return () => clearInterval(timer);
  }, [turnstileSiteKey]);

  const resetCaptcha = () => {
    setCaptchaToken('');
    if (window.turnstile && captchaWidgetId.current !== null) {
      window.turnstile.reset(captchaWidgetId.current);
    }
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitRegistration = async (event) => {
    event.preventDefault();
    if (!captchaToken) {
      setCaptchaError('Completá el captcha antes de crear el alumno.');
      return;
    }

    setSubmitting(true);
    setAlert((prev) => ({ ...prev, show: false }));
    try {
      await particularesApiService.registrarAlumnoPorInvitacion({
        codigo,
        ...form,
        captchaToken
      });
      setSuccess(true);
      setForm(emptyForm);
      resetCaptcha();
      setAlert({
        show: true,
        type: 'success',
        title: 'Registro creado',
        message: 'Ya podés ingresar con tu email y usar tu DNI como contraseña inicial.'
      });
    } catch (error) {
      setAlert({
        show: true,
        type: 'error',
        title: 'No pudimos crear el alumno',
        message: error.message || 'Revisá los datos e intentá nuevamente.'
      });
      resetCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="particular-invite-page">
      <div className="particular-invite-shell">
        <aside className="particular-invite-intro">
          <div className="particular-invite-brand">
            <img src={brandLogo} alt="aluxIA" />
            <div>
              <span>aluxIA</span>
              <h1>Alta de alumno particular</h1>
            </div>
          </div>

          <div className="particular-invite-intro-copy">
            <span className="particular-invite-kicker">Invitación privada</span>
            <h2>{particularName}</h2>
            <p>Completá tus datos para que el profesor te habilite dentro de sus cursos y aulas de trabajo.</p>
          </div>

          <div className="particular-invite-steps">
            <article>
              <i className="fas fa-ticket-alt" />
              <div>
                <strong>Validá el código</strong>
                <span>Confirmamos que la invitación exista y siga vigente.</span>
              </div>
            </article>
            <article>
              <i className="fas fa-user-check" />
              <div>
                <strong>Creá tu acceso</strong>
                <span>Tu email será el usuario y tu DNI la contraseña inicial.</span>
              </div>
            </article>
            <article>
              <i className="fas fa-shield-alt" />
              <div>
                <strong>Protección anti spam</strong>
                <span>El captcha se valida de forma segura antes del alta.</span>
              </div>
            </article>
          </div>
        </aside>

        <section className="particular-invite-panel">
          <div className="particular-invite-form-header">
            <span>Registro</span>
            <h2>Completá tu alta</h2>
            <p>Si es tu primer acceso, después de registrarte entrá al login con tu email y tu DNI.</p>
          </div>

          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
            />
          )}

          <form className="particular-invite-form" onSubmit={submitRegistration}>
            <div className="particular-invite-code-row">
              <label>
                Código de invitación
                <input
                  value={codigo}
                  onChange={(event) => {
                    setCodigo(event.target.value);
                    setInvitacion(null);
                    setSuccess(false);
                  }}
                  minLength={6}
                  required
                />
              </label>
              <button type="button" className="particular-invite-secondary" onClick={() => validateCode()} disabled={validating || !codigo.trim()}>
                {validating ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                Validar
              </button>
            </div>

            {validating && <LoadingSpinner message="Validando invitación..." size="small" inline />}

            {invitacion && (
              <div className="particular-invite-valid">
                <i className="fas fa-check-circle" />
                <div>
                  <strong>Invitación válida</strong>
                  <span>{particularName}</span>
                </div>
              </div>
            )}

            <div className="particular-invite-grid">
              <label>Email<input type="email" value={form.email} onChange={(event) => updateForm('email', event.target.value)} required /></label>
              <label>DNI<input value={form.dni} onChange={(event) => updateForm('dni', event.target.value)} required /></label>
              <label>Nombre<input value={form.nombre} onChange={(event) => updateForm('nombre', event.target.value)} /></label>
              <label>Apellido<input value={form.apellido} onChange={(event) => updateForm('apellido', event.target.value)} /></label>
              <label>Teléfono<input value={form.telefono} onChange={(event) => updateForm('telefono', event.target.value)} /></label>
            </div>

            <div className="particular-invite-captcha">
              <div>
                <strong>Verificación</strong>
                <span>Completá el captcha para finalizar el registro.</span>
              </div>
              <div ref={captchaRef} className="turnstile-slot particular-turnstile-slot" />
              {captchaError && <div className="particular-invite-error">{captchaError}</div>}
            </div>

            <div className="particular-invite-note">
              <i className="fas fa-key" />
              <span>Para el primer acceso tu contraseña será tu DNI. Luego deberás cambiarla desde tu cuenta.</span>
            </div>

            <div className="particular-invite-actions">
              <Link className="particular-invite-secondary" to="/login">Ir al login</Link>
              <button type="submit" className="particular-invite-primary" disabled={submitting || validating || !codigo.trim() || !captchaToken || success}>
                {submitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-user-plus" />}
                {success ? 'Registro creado' : 'Crear alumno'}
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
};

export default ParticularAlumnoInvitationRegister;
